import { useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { ArrowSquareOut, ArrowsClockwise, Prohibit, Warning } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  active: { label: "Active", bg: "var(--bk-success-bg)", fg: "var(--bk-success)" },
  due_soon: { label: "Renewal due", bg: "rgba(232,168,56,0.16)", fg: "#9A6B15" },
  expired: { label: "Expired", bg: "#FFF5F2", fg: "#B23B1F" },
  renewed: { label: "Renewed", bg: "var(--bk-page)", fg: "var(--bk-text-2)" },
  void: { label: "Void", bg: "var(--bk-page)", fg: "var(--bk-text-2)" },
};

export function ConsentRecords({ businessId, records, filter, onRenew, onChanged }) {
  const token = localStorage.getItem("bk_token");
  const [voiding, setVoiding] = useState(null);
  const rows = records.filter((r) => filter === "all" ? true : filter === "attention" ? ["due_soon", "expired"].includes(r.renewal_status) : r.renewal_status === filter);

  const doVoid = async (rec) => {
    const reason = window.prompt(`Void ${rec.client_name}'s ${rec.service_label || "consent"}? Add a short reason — it stays on the record.`);
    if (reason === null) return;
    setVoiding(rec.id);
    try { await api.post(`/businesses/${businessId}/consents/records/${rec.id}/void`, { reason }); toast.success("Consent voided."); onChanged?.(); }
    catch (e) { toast.error(describeApiError(e, "Couldn't void that record.")); }
    finally { setVoiding(null); }
  };

  if (!rows.length) {
    return <p className="text-sm rounded-xl border p-4" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="consent-records-empty">
      {filter === "all" ? "No signed consents yet. Publish a form, then use Collect signature." : "Nothing here — no consents match this filter."}
    </p>;
  }
  return (
    <div className="space-y-2" data-testid="consent-records">
      {rows.map((r) => {
        const st = STATUS[r.renewal_status] || STATUS.active;
        return (
          <div key={r.id} className="rounded-xl border p-3 flex flex-wrap items-center gap-3 bg-white" style={{ borderColor: r.renewal_status === "expired" ? "#F3C9BE" : "var(--bk-border)" }} data-testid={`consent-record-${r.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.fg }} data-testid={`consent-status-${r.id}`}>{st.label}</span>
                {r.flags_count > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-energy-bg)", color: "#8A5A13" }} title={r.clearance ? `Cleared by ${r.clearance.cleared_by}: ${r.clearance.note}` : ""}><Warning size={11} weight="fill" /> {r.flags_count} screening flag{r.flags_count === 1 ? "" : "s"}</span>}
              </div>
              <p className="text-sm font-semibold mt-1" style={{ color: "var(--bk-text)" }}>{r.client_name}{r.client_email ? <span className="font-normal" style={{ color: "var(--bk-text-2)" }}> · {r.client_email}</span> : null}</p>
              <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>
                {r.template_title} · form version {r.version} · signed {r.signed_on} · {r.renewal_status === "expired" ? `expired ${r.expires_on}` : `valid until ${r.expires_on}`}{typeof r.days_to_expiry === "number" && r.renewal_status === "due_soon" ? ` (${r.days_to_expiry} days)` : ""} · with {r.practitioner_name}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {r.vault_doc_id && (
                <a href={`${BACKEND}/api/vault/${r.vault_doc_id}/file?auth=${token}`} target="_blank" rel="noopener noreferrer" data-testid={`consent-open-pdf-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
                  <ArrowSquareOut size={13} weight="bold" /> Signed PDF
                </a>
              )}
              {["active", "due_soon", "expired"].includes(r.renewal_status) && (
                <button onClick={() => onRenew(r)} data-testid={`consent-renew-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-white" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}>
                  <ArrowsClockwise size={13} weight="bold" /> Renew
                </button>
              )}
              {r.renewal_status !== "void" && (
                <button onClick={() => doVoid(r)} disabled={voiding === r.id} data-testid={`consent-void-${r.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-white disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-energy)" }}>
                  <Prohibit size={13} weight="bold" /> Void
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
